import type { CalculationKind, CalculationRecord } from "../../../domain/engineering/calculation-record.js";

const STORAGE_KEY = "engineering_calculations";

export interface CalculationRepository {
  findById(id: string): Promise<CalculationRecord | null>;
  listByKind(kind: CalculationKind): Promise<CalculationRecord[]>;
  countByKind(kind: CalculationKind): Promise<number>;
  save(record: CalculationRecord): Promise<void>;
}

/** History of the three calculators. Records are kept apart by kind and never merged. */
export class InMemoryCalculationRepository implements CalculationRepository {
  private records = new Map<string, CalculationRecord>();

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage() {
    try {
      if (typeof localStorage !== "undefined") {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed: CalculationRecord[] = JSON.parse(stored);
          for (const record of parsed) this.records.set(record.id, record);
        }
      }
    } catch (e) {
      console.warn("Failed to load engineering calculations from storage:", e);
    }
  }

  private persistStorage() {
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(this.records.values())));
      }
    } catch (e) {
      console.warn("Failed to persist engineering calculations to storage:", e);
    }
  }

  async findById(id: string): Promise<CalculationRecord | null> {
    this.loadFromStorage();
    return this.records.get(id) || null;
  }

  async listByKind(kind: CalculationKind): Promise<CalculationRecord[]> {
    this.loadFromStorage();
    return Array.from(this.records.values())
      .filter(r => r.kind === kind)
      .sort((a, b) => b.performedAt.localeCompare(a.performedAt));
  }

  /** Next sequence for a kind — each calculation numbers itself independently. */
  async countByKind(kind: CalculationKind): Promise<number> {
    this.loadFromStorage();
    return Array.from(this.records.values()).filter(r => r.kind === kind).length;
  }

  async save(record: CalculationRecord): Promise<void> {
    this.records.set(record.id, { ...record });
    this.persistStorage();
  }
}
